'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Container from '../../layout/Container';
import { emailSubscription } from '@/app/api/emailSubscription/emailSubscription';
import elevarSubscribe from '@/lib/elevar/subscribe';

function NewsletterForm({ data, footer }) {
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  const [inputs, setInputs] = useState({
    subscribe__email: ''
  });

  const handleResponse = (status, msg) => {
    if (status === 201 || status === 200) {
      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: msg }
      });
      setInputs({
        subscribe__email: ''
      });
    } else {
      setStatus({
        info: { error: true, msg: msg }
      });
    }
  };

  const handleOnChange = (e) => {
    e.persist();
    setInputs((prev) => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
    setStatus({
      submitted: false,
      submitting: false,
      info: { error: false, msg: null }
    });
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault();
    setStatus((prevStatus) => ({ ...prevStatus, submitting: true }));

    const $email = inputs.subscribe__email;

    if (!$email.match(/\S+@\S+/)) {
      handleResponse(
        400,
        `There's an issue with your provided email address.<br/> Please try again.`
      );
      return;
    }

    try {
      const res = await emailSubscription({ email: $email });
      if (!res.success) {
        handleResponse(
          res.status,
          res.message ??
            "There's an issue signing up for the newsletter. <br/> Please try again."
        );
        return;
      } else {
        handleResponse(res.status, res.message ?? 'Thank you for signing up!');
        elevarSubscribe($email);
      }
    } catch (e) {
      // Network and runtime errors on the client side
      console.error('Client-side error when subscribing:', e);
      handleResponse(400, 'There was a network issue. Please try again.');
    }
  };

  return (
    <>
      <form
        onSubmit={handleOnSubmit}
        className={`my-4 ${!footer ? 'relative' : 'flex-col'}`}
        data-form="email-subscribe-form"
        data-track-submit="email-subscribe-form"
      >
        <div className="flex flex-row justify-between">
          <label htmlFor="subscribe__email" className="sr-only">
            {data.newsletter.title}
          </label>
          <input
            id="subscribe__email"
            className={`input-primary font-lato h-11 bg-white p-2 ${!footer ? 'mb-4 w-72 rounded text-center' : 'placeholder:text-default mb-2 w-full px-4 placeholder:text-left placeholder:text-sm lg:w-60'}`}
            type="email"
            onChange={handleOnChange}
            value={inputs.subscribe__email}
            placeholder={data.newsletter.input_placeholder_label}
          />
          {!footer ? (
            <div>
              <button
                id="contact-form-submit"
                type="submit"
                disabled={status.submitting}
                className="border-grey-dark text-default -mb-2 border-0 border-b-2 font-semibold transition duration-500 ease-in-out hover:pb-1"
              >
                {!status.submitting
                  ? !status.submitted
                    ? data.newsletter.form_submit_label
                    : 'Subscribed'
                  : 'Please wait'}
              </button>
            </div>
          ) : (
            <button
              id="contact-form-submit"
              type="submit"
              disabled={status.submitting}
              className={`btn-primary font-lato relative h-11 appearance-none px-5 transition-shadow hover:shadow-md sm:px-8`}
            >
              {!status.submitting
                ? !status.submitted
                  ? data.newsletter.form_submit_label
                  : 'Subscribed'
                : 'Please wait'}
            </button>
          )}
        </div>
        <div>
          {status.info.error && (
            <div className="success flex w-full grow-0 flex-wrap pt-1 text-center">
              <span
                className="font-lato mx-auto text-center text-xs"
                dangerouslySetInnerHTML={{ __html: status.info.msg }}
              />
            </div>
          )}
          {!status.info.error && status.info.msg && (
            <div className="success flex w-full grow-0 flex-wrap pt-1 text-center">
              <span
                className="font-lato mx-auto text-center text-xs"
                dangerouslySetInnerHTML={{ __html: status.info.msg }}
              />
            </div>
          )}
        </div>
      </form>
    </>
  );
}

export default function Subscribe({ data, footer }) {
  if (!footer) {
    const { reference } = data.subscribe;
    const margin = reference[0].add_margin_to_top ? 'pt-36' : '';

    return (
      <>
        <section id="subscribe" className={`relative pb-16 sm:pb-0 ${margin}`}>
          <Container fullPage={false}>
            <div className="relative w-full">
              <div className="standard-padding border-grey-dark absolute -top-40 right-0 left-0 mx-auto flex max-w-5xl flex-col items-center justify-evenly border bg-white px-6 text-center sm:-top-28 sm:flex-row">
                {!reference[0].hide_refer_a_friend ? (
                  <>
                    <div id="refer" className="mb-8 flex-none sm:mb-0">
                      <span className="mb-6 block font-semibold">
                        {reference[0].refer_a_friend.title}
                      </span>
                      {reference[0].refer_a_friend.link[0].url && (
                        <div>
                          <Link
                            href={reference[0].refer_a_friend.link[0].url}
                            prefetch={false}
                            className="ease border-grey-dark text-default -mb-2 inline-block border-b-2 font-semibold transition hover:pb-1"
                          >
                            {reference[0].refer_a_friend.earning_copy}
                          </Link>
                        </div>
                      )}
                    </div>
                    <div className="subscribe__divider"></div>
                  </>
                ) : (
                  ''
                )}
                <div id="newsletter" className="flex-none grow-0">
                  <span className="text-default font-semibold">
                    {reference[0].newsletter.title}
                  </span>

                  <NewsletterForm data={reference[0]} />
                </div>
              </div>
            </div>
          </Container>
        </section>
      </>
    );
  } else {
    return (
      <>
        <section id="subscribe" className="mb-8 pt-8 sm:pt-[.85rem]">
          <div id="newsletter">
            <span className="sh12 sm:sh11">{data.newsletter.title}</span>

            <NewsletterForm data={data} footer={true} />
          </div>
        </section>
      </>
    );
  }
}
